import { MdClose } from "react-icons/md";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import Cookies from "js-cookie";
import { CalendarIcon, Plus } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { useCreateTodoModal } from "./use-create-todo-modal";
import { Textarea } from "@/components/ui/textarea/textarea";
import { Calendar } from "@/components/ui/calendar/calendar";
import { Button } from "@/components/ui/button/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { getAllEmployeesQueryOptions } from "@/config/queries/query-options";

import MultipleSelector from "@/components/ui/multiple-selector/multiple-selector";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator/separator";
import { AddTasks } from "./add-tasks";
import { createNewTodo } from "@/config/mutation/admin";
import { UploadAttachment } from "./upload-attachement";

const formSchema = z.object({
  title: z.string().min(2).max(50),
  description: z.string(),
  recipients: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    })
  ),
  deadline: z.date(),
  tasks: z.array(
    z
      .object({
        description: z.string(),
        recipient: z.object({
          value: z.string(),
          label: z.string(),
        }),
        assignee: z.string(),
        files: z.array(z.string()).optional(),
      })
      .optional()
  ),
  files: z.array(z.string()).optional(),
});

export type FormValues = z.infer<typeof formSchema>;

export const CreateTodoModal = () => {
  const { open, onOpenChange } = useCreateTodoModal();
  const user = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      recipients: [],
      deadline: undefined,
      tasks: [],
      files: [],
    },
  });

  const recipients = useWatch({
    name: "recipients",
    control: form.control,
  });

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: createNewTodo(user),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    //    console.log(values);

    if (!values?.title) {
      return toast.error("Please provide todo's title");
    }

    if (!values?.description) {
      return toast.error("Please provide todo's description");
    }

    if (values?.recipients?.length === 0) {
      return toast.error("Please select assignee");
    }

    const realData = {
      title: values?.title,
      description: values?.description,
      deadline: values?.deadline,
      recipients: values?.recipients?.map((r) => r.value),
      tasks: values?.tasks?.map((t) => ({
        description: t?.description,
        recipient: t?.recipient?.value,
        files: t?.files,
      })),
      files: values?.files,
    };

    mutate(realData, {
      onSuccess: async () => {
        // console.log("New Employee", data);
        await queryClient.invalidateQueries({
          queryKey: ["todos"],
        });

        toast.success("New TODO Created!");
        onOpenChange(false);

        form.reset();
      },

      onError: (error) => {
        console.log("ERROR WHILE CREATING TODO", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  }

  const { data, isPending, isFetching } = useQuery(
    getAllEmployeesQueryOptions(user)
  );

  const employeesData = useMemo(() => {
    if (isPending || isFetching) {
      return [];
    }

    if (data === undefined || data?.employees === undefined) {
      return [];
    }

    const result = data?.employees?.map((emp) => {
      return {
        value: emp?._id,
        label: emp?.name,
      };
    });

    return result;
  }, [data, isPending, isFetching]);

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogTrigger>
          <Button className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Todo
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="p-0 max-w-[1400px]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="border-b px-3 py-2 flex items-center justify-between">
                <h3 className="text-xl font-medium tracking-normal">
                  Create New Todo
                </h3>
                <div className="relative flex items-center gap-4">
                  <Button size="sm" disabled={isSubmitting}>
                    Create
                  </Button>
                  <button
                    disabled={isSubmitting}
                    type="button"
                    className="p-1 rounded-md transition-all duration-300 hover:bg-accent"
                    onClick={() => {
                      onOpenChange(false);
                      form.reset();
                    }}
                  >
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="flex">
                <div className="flex-[0.6] p-5 space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            disabled={isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            rows={5}
                            disabled={isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <AddTasks assignees={employeesData} />
                </div>
                <div className="flex-[0.4] border-l p-5 space-y-3">
                  <FormField
                    control={form.control}
                    name="deadline"
                    render={({ field }) => (
                      <FormItem className="flex flex-col space-y-0 gap-3 mt-1">
                        <FormLabel>Deadline</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                disabled={isPending || isSubmitting}
                                variant={"outline"}
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={(day) => {
                                field.onChange(day);
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col">
                    <h4 className="text-sm mb-2 font-medium">Assignees</h4>
                    <MultipleSelector
                      disabled={isPending || isSubmitting}
                      options={employeesData}
                      value={recipients}
                      onChange={(options) => {
                        form.setValue("recipients", options);
                      }}
                      emptyIndicator={
                        <p className="text-center text-lg text-gray-600 dark:text-gray-400">
                          no more employees found.
                        </p>
                      }
                    />
                  </div>

                  <div>
                    <FormField
                      control={form.control}
                      name="files"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Upload Attachment</FormLabel>
                          <FormControl>
                            <UploadAttachment
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
